import React from "react"
import classnames from "classnames"
import { Option } from "./Option"
import { OptGroup } from "./OptGroup"

interface OptionItem {
  label: string
  value: string | number
  disabled?: boolean
}

export interface SelectProps {
  // Common props
  id?: string
  name?: string
  value: string | number
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  disabled?: boolean
  ariaLabel?: string
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode

  // From local Select (options array pattern)
  options?: OptionItem[]
  readOnly?: boolean

  // From doxy Select (children pattern)
  label?: string
  size?: "md" | "sm"
  required?: boolean
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void
  wrapperClassName?: string
}

const Options = ({ options }: { options: OptionItem[] }) => (
  <>
    {options.map((option) => (
      <option
        key={option.value}
        value={option.value}
        disabled={option.disabled}
      >
        {option.label}
      </option>
    ))}
  </>
)

const Select = ({
  id,
  name,
  value,
  onChange,
  disabled = false,
  ariaLabel,
  className = "select",
  style,
  children,
  options,
  readOnly,
  label,
  size = "md",
  required = false,
  onBlur,
  wrapperClassName,
}: SelectProps) => {
  const selectId = id || name

  const selectClassName = classnames(className, {
    [`${size}-select`]: size !== "md" || label !== undefined,
    readOnly,
  })

  return (
    <div className={classnames("dds-field-wrapper", wrapperClassName)}>
      {label && (
        <label htmlFor={selectId}>
          {label}
          {required ? <span className="c-ruby ml-4p">*</span> : null}
        </label>
      )}
      <select
        aria-label={ariaLabel}
        className={selectClassName}
        disabled={disabled}
        id={selectId}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        required={required}
        style={style}
        value={value}
      >
        {children || (options && <Options options={options} />)}
      </select>
    </div>
  )
}

Select.Option = Option
Select.OptGroup = OptGroup
Select.Options = Options

export { Select }
